import { WellStatusChartWidgetKey } from "components/dashboard/constants/widgets.constants";

import { useChartWidgetSession } from "./useChartWidgetSession";

/**
 * Session key prefix. Imported from WidgetKeys rather than restated as a literal
 * so the "must equal the widget's type" invariant is structural — the dashboard
 * reads this key by that exact string, and a mismatch persists nothing at all,
 * silently.
 */
export const WELL_STATUS_SESSION_KEY_PREFIX = WellStatusChartWidgetKey;

/**
 * The user's SELECTIONS. Deliberately excludes `filterId` and `focus`: both are
 * live dashboard state the controller re-derives on mount, and freezing them into
 * a saved dashboard would pin it to whichever filter and grouping happened to be
 * active when it was saved.
 */
const PERSISTED_REQUEST_KEYS = [
  "chartType",
  "status",
  "allStatuses",
  "timeScale",
  "customRange",
  "includeDrainLegs"
] as const;

/**
 * What the over-time chart's y-axis COUNTS, stamped into every saved widget.
 *
 * The axis changed meaning once (EVA-42): it used to count wells HOLDING the
 * selected status at each period end, and now counts wells ENTERING it during the
 * period. Those magnitudes differ by one to two orders — thousands of producing
 * wells versus a handful starting in a given month — so a y-max lock saved
 * against the old numbers reopens the widget with every bar flattened to
 * invisibility, with no error and no toast to explain it.
 *
 * Settings written from this release carry the stamp; anything saved before it
 * does not, which is what lets the wrapper drop exactly the stale locks and keep
 * every lock set since. Bump this string if the axis ever changes meaning again.
 */
export const WELL_STATUS_COUNTS_MODEL = "entries";

/**
 * Persists the Well Status widget's config for the dashboard save round-trip.
 *
 * Thin wrapper over {@link useChartWidgetSession}; the two widgets that use it
 * differ only in their key and their persisted request fields. See that hook for
 * why the write half matters and what is deliberately left out.
 */
export function useWellStatusSession(id: string) {
  useChartWidgetSession(id, WELL_STATUS_SESSION_KEY_PREFIX, PERSISTED_REQUEST_KEYS, {
    countsModel: WELL_STATUS_COUNTS_MODEL
  });
}
